import { MascotTip } from "@/components/Mascot";
import { OrderHelp } from "@/components/SupportEmail";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Coins, Leaf, FlaskConical, HeartPulse, Gem } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useGame } from "@/context/GameContext";
import EnergyPanel from "@/components/EnergyPanel";
import PupSale from "@/components/PupSale";
import UniqueLooks from "@/components/UniqueLooks";

const ITEM_ICONS = { herb: Leaf, atk_tonic: FlaskConical, def_tonic: FlaskConical, revive: HeartPulse };

export default function Store() {
  const { game, refresh } = useGame();
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(null);
  const [packsOn, setPacksOn] = useState(false);
  useEffect(() => {
    api.get("/payments/packs").then(({ data }) => setPacks(data));
    api.get("/sales/switches").then(({ data }) => setPacksOn(data.packs));
  }, []);

  const checkout = async (pack_id) => {
    setBusy(pack_id);
    try {
      const { data } = await api.post("/payments/checkout", { pack_id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };
  const buy = async (item) => {
    setBusy(item);
    try { await api.post("/shop/buy", { item }); await refresh(); toast.success("Added to your satchel"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] font-mono-game text-amber-400/80 mb-3">Trader's Hollow</p>
          <h1 className="font-display text-4xl sm:text-5xl text-amber-100">Credits Store</h1>
          <div className="mt-3 [&>p]:justify-start"><OrderHelp testid="store-order-help" /></div>
        </div>
        <p className="flex items-center gap-2 text-amber-200 font-mono-game text-2xl" data-testid="store-credit-balance"><Coins className="w-6 h-6" />{game.user.credits.toLocaleString()}</p>
      </div>

      <section className="grid md:grid-cols-3 gap-5 mb-16">
        {packs.map((p, i) => (
          <div key={p.id} data-testid={`credit-pack-${p.id}`} className={`relative rounded-2xl p-7 border ${i === 1 ? "border-fuchsia-500/60 shadow-[0_0_40px_rgba(217,70,239,.2)]" : "border-slate-700"} bg-gradient-to-b from-slate-900 to-slate-950`}>
            {i === 1 && <span className="absolute -top-3 left-7 px-3 py-0.5 rounded-full bg-fuchsia-500 text-slate-950 text-[11px] font-bold">POPULAR</span>}
            <Gem className={`w-8 h-8 mb-6 ${["text-fuchsia-300", "text-amber-300", "text-yellow-200"][i]}`} />
            <p className="font-display text-xl text-slate-100">{p.name}</p>
            <p className="font-mono-game text-4xl text-white mt-3">{p.credits.toLocaleString()}<span className="text-sm text-slate-400 ml-2">credits</span></p>
            <button data-testid={`buy-pack-${p.id}`} disabled={!packsOn || busy === p.id} onClick={() => checkout(p.id)} className="ember-btn w-full py-3 rounded-lg text-sm mt-8 disabled:opacity-50">
              {!packsOn ? `COMING SOON · $${(p.price_cents / 100).toFixed(2)}` : busy === p.id ? "OPENING CHECKOUT…" : `BUY · $${(p.price_cents / 100).toFixed(2)}`}
            </button>
          </div>
        ))}
      </section>

      {!packsOn && <MascotTip testid="store-packs-mascot-tip" className="-mt-10 mb-16">Credit packs are almost here. Until then, keep climbing: daily rewards, quests and your lucky spin all fill your den.</MascotTip>}
      <PupSale game={game} drop="rare" />
      <PupSale game={game} drop="exclusive" />
      <PupSale game={game} drop="wolfpack" />
      <PupSale game={game} drop="pup" />
      {game.wolf && game.energy && (
        <section className="mb-12 max-w-md" data-testid="store-energy">
          <h2 className="font-display text-2xl text-amber-100 mb-2">Energy</h2>
          <p className="text-sm text-slate-400 mb-4">Every fight, hunt and event costs 1 energy. It refills slowly over time; top up to keep climbing today.</p>
          <EnergyPanel game={game} refresh={refresh} />
        </section>
      )}
      <h2 className="font-display text-2xl text-amber-100 mb-2">Spend credits</h2>
      <p className="text-sm text-slate-400 mb-6">Credit boosts are short-term and capped. NFT boosts are permanent. Revive in battle costs {game.revive_cost} credits, event re-rolls cost {game.reroll_cost}.</p>
      {!game.wolf && <p className="text-sm text-amber-300 mb-4">Create your wolf to buy items.</p>}
      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
        {Object.entries(game.shop).map(([id, it]) => {
          const Icon = ITEM_ICONS[id];
          return (
            <div key={id} className="glass rounded-2xl p-5 flex flex-col" data-testid={`shop-item-${id}`}>
              <Icon className="w-6 h-6 text-emerald-300 mb-4" />
              <p className="font-semibold">{it.name}</p>
              <p className="text-xs text-slate-400 mt-1 flex-1">{it.desc}</p>
              <p className="text-[11px] text-slate-500 mt-2">Owned: {game.wolf?.inventory?.[id] ?? 0}</p>
              <button data-testid={`buy-item-${id}`} disabled={!game.wolf || busy === id || game.user.credits < it.price} onClick={() => buy(id)}
                className="mt-3 w-full py-2 rounded-lg border border-amber-500/40 text-amber-200 text-sm hover:bg-amber-500/10 disabled:opacity-40">{it.price.toLocaleString()} credits</button>
            </div>
          );
        })}
      </section>

      <UniqueLooks />
    </main>
  );
}
